import { message } from 'antd'
import axios from 'axios'
import loaderRef from './loaderRef'
import AppConfig from '../config'
import { clearSession, getAccessToken, getRefreshToken, setSession } from './session'

let loaderCount = 0
let refreshing = null

const apiClient = axios.create({
  baseURL: `${AppConfig.API_URL}/`
})

apiClient.interceptors.request.use(
  (config) => {
    if (!config.skipLoader) {
      loaderRef.toggleLoader(true)
      loaderCount += 1
    }

    return {
      ...config,
      headers: {
        Authorization: config.headers.Authorization || `Bearer ${getAccessToken()}`
      }
    }
  },
  (error) => Promise.reject(error)
)

const tryRefreshToken = async () => {
  const refreshToken = getRefreshToken()

  if (!refreshToken) {
    return null
  }

  const { data } = await axios.post(`${AppConfig.API_URL}/auth/refresh`, { refreshToken })

  if (data?.token) {
    setSession(data.token, refreshToken)

    return data.token
  }

  return null
}

apiClient.interceptors.response.use(
  (response) => {
    if (!response.config?.skipLoader) {
      loaderRef.toggleLoader(loaderCount !== 1)
      loaderCount -= 1
    }

    return response
  },
  async (error) => {
    if (!error.config?.skipLoader) {
      loaderRef.toggleLoader(false)
      loaderCount -= 1
    }

    const { response, config } = error

    if (!response) {
      return Promise.reject(error)
    }

    const { data, status } = response

    if (status === 401 && !config?._retry) {
      if (!refreshing) {
        refreshing = tryRefreshToken().finally(() => {
          refreshing = null
        })
      }

      const newToken = await refreshing

      if (newToken) {
        config._retry = true
        config.headers.Authorization = `Bearer ${newToken}`

        return apiClient(config)
      }

      message.error(data?.message || 'Session expired')
      clearSession()
      window.location.href = '/'

      return Promise.reject(error)
    }

    if (status === 403 && data?.code === 'ORGANIZATION_SUSPENDED') {
      message.error(data?.message || 'Your organization has been suspended.')
      clearSession()

      if (window.location.pathname.startsWith('/app')) {
        window.location.href = '/'
      }

      return Promise.resolve({ status, ...data })
    }

    if (status === 403 && data?.code === 'PASSWORD_CHANGE_REQUIRED') {
      if (!window.location.pathname.startsWith('/app/changePassword')) {
        window.location.href = '/app/changePassword?required=1'
      }

      return Promise.resolve({ status, ...data })
    }

    if (
      status === 403 &&
      (data?.code === 'ADMIN_MFA_REQUIRED' || data?.code === 'MFA_SETUP_REQUIRED')
    ) {
      if (!window.location.pathname.startsWith('/app/profile')) {
        window.location.href = '/app/profile?mfaRequired=1'
      }

      return Promise.resolve({ status, ...data })
    }

    if (status >= 400 && status < 500) {
      if (data?.errors && Object.keys(data.errors).length > 0) {
        errorsParser(data.errors)
      } else if (data?.message) {
        message.error(data.message)
      }

      return Promise.resolve({ status, ...data })
    }

    if (status >= 500) {
      errorsParser(data?.errors)

      return Promise.resolve({ status, ...data })
    }

    return Promise.reject(error)
  }
)

const errorsParser = (errors) => {
  if (errors) {
    if (typeof errors === 'string') {
      message.error(errors)
    } else {
      Object.values(errors).forEach((err) => {
        message.error(err)
      })
    }
  }
}

export default apiClient
